import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getInitials } from './initials';
import type { ConversationThread } from '@/types/omniChannel';

interface ThreadItemProps {
  thread: ConversationThread;
  isSelected: boolean;
  onClick: () => void;
}

export function ThreadItem({ thread, isSelected, onClick }: ThreadItemProps) {
  const hasUnread = thread.unread_count > 0;
  const isSms = thread.last_message_type === 'sms';
  const ChannelIcon = isSms ? MessageSquare : Mail;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2.5 border-b border-border transition-colors',
        'hover:bg-muted/50 focus:outline-none focus-visible:bg-muted/50',
        isSelected ? 'bg-primary/10' : hasUnread && 'bg-primary/5'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Initials avatar */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
          {getInitials(thread.contact_name)}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Name + time */}
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                'truncate text-sm',
                hasUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'
              )}
            >
              {thread.contact_name}
            </span>
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {formatDistanceToNow(new Date(thread.last_message_at), { addSuffix: true })}
            </span>
          </div>

          {/* Channel icon + preview + unread dot */}
          <div className="mt-0.5 flex items-center gap-1.5">
            <ChannelIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
            <p
              className={cn(
                'min-w-0 flex-1 truncate text-xs',
                hasUnread ? 'font-medium text-foreground' : 'text-muted-foreground'
              )}
            >
              {thread.last_message || 'No message content'}
            </p>
            {hasUnread && (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-primary"
                title={`${thread.unread_count} unread`}
              />
            )}
          </div>

          {/* Lead reference */}
          {thread.lead_number && (
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {thread.lead_number}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
